/**
 * Data models for round documents (captables and closing documents)
 */

/**
 * Document types that can be uploaded for a round
 */
export type RoundDocumentType = 'captable' | 'closing_doc' | 'side_letter';

/**
 * Extraction-pipeline states per document, in stage order (null = stage not reached):
 * Starbreaker extraction → parsing → mapping.
 */
export type DealDocProcessingStatus = 'processed' | 'failed';
export type DealDocParsingStatus = 'pending' | 'parsed' | 'failed';
export type DealDocMappingStatus = 'pending' | 'mapped' | 'failed';

/**
 * Round status based on document availability
 */
export type RoundStatus = 'pending_docs' | 'docs_uploaded' | 'data_extracted' | 'extraction_failed';

/** Display status of one document, folded down from the pipeline states. */
export type DealDocDisplayStatus = 'Uploaded' | 'Processing' | 'Extracted' | 'Failed' | 'Incomplete Set';

/**
 * Fold a document's pipeline states into one display status. Cap tables and side
 * letters finish at extraction; closing docs go on through parsing and mapping.
 */
export function deriveDealDocDisplayStatus(doc: IRoundDocumentMetadata): DealDocDisplayStatus {
  // Starbreaker will not extract until the package holds every mandatory document; read this
  // ahead of the pipeline states, which an earlier failed run may have left behind.
  if (doc.isPackageComplete === false) {
    return 'Incomplete Set';
  }
  if (doc.processingStatus === 'failed' || doc.parsingStatus === 'failed' || doc.mappingStatus === 'failed') {
    return 'Failed';
  }
  if (doc.documentType === 'closing_doc') {
    if (doc.mappingStatus === 'mapped') {
      return 'Extracted';
    }
    if (doc.processingStatus === 'processed' || doc.parsingStatus != null || doc.mappingStatus != null) {
      return 'Processing';
    }
    return 'Uploaded';
  }
  return doc.processingStatus === 'processed' ? 'Extracted' : 'Uploaded';
}

/**
 * Request payload for requesting an S3 upload URL
 */
export interface IRoundDocumentUploadUrlRequest {
  documentType: RoundDocumentType;
  fileName: string;
  contentType: string;
}

/**
 * Response from requesting an S3 upload URL
 */
export interface IRoundDocumentUploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  s3Key: string;
}

/**
 * Request payload for confirming a document upload
 */
export interface IRoundDocumentConfirmRequest {
  documentType: RoundDocumentType;
  fileId: string;
  fileName: string;
  s3Key: string;
  fileSize: number;
  contentType: string;
}

/**
 * Metadata for a round document
 */
export interface IRoundDocumentMetadata {
  id: number;
  roundId: number;
  documentType: RoundDocumentType;
  fileName: string;
  fileSize: string; // Formatted string like "1.00 MB"
  contentType: string;
  s3Key?: string;
  uploadedBy: string;
  uploadSource: string;
  processingStatus?: DealDocProcessingStatus | null;
  parsingStatus?: DealDocParsingStatus | null;
  mappingStatus?: DealDocMappingStatus | null;
  extractedAt?: string | null;
  /** Starbreaker's mandatory-document verdict: false = waiting on documents, null = never judged. */
  isPackageComplete?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response from document download URL request
 */
export interface IRoundDocumentDownloadUrlResponse {
  downloadUrl: string;
}

/**
 * Response from document deletion
 */
export interface IRoundDocumentDeleteResponse {
  message: string;
}

/**
 * Request payload for batch upload URL request
 */
export interface IBatchRequestUploadUrlRequest {
  files: IRoundDocumentUploadUrlRequest[];
}

/**
 * Response from batch upload URL request
 */
export interface IBatchRequestUploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  s3Key: string;
}

/**
 * Request payload for batch confirm upload
 */
export interface IBatchConfirmUploadRequest {
  uploads: IRoundDocumentConfirmRequest[];
}

/**
 * Response from batch confirm upload
 */
export interface IBatchConfirmUploadResponse {
  success: IRoundDocumentMetadata[];
  errors: Array<{ fileName: string; error: string }>;
}
